/**
 * Service-worker registration.
 *
 * Goals (Phase 3 P0 §1.6):
 *   1. Pre-cache the SPA shell + hashed assets on first install so
 *      a hard reload while offline still loads the SPA.
 *   2. Stale-while-revalidate the runtime-config so feature flags
 *      can flip without a redeploy.
 *   3. Fall back to the cached index.html for SPA navigation when
 *      the network is down.
 *
 * Non-goals (deliberately deferred):
 *   - Cross-tab message channel (the SPA already uses BroadcastChannel
 *     and storage events for live-data sync; the SW doesn't need to).
 *   - Background sync (the api-server doesn't yet define sync
 *     endpoints; revisit when it does).
 *   - Push notifications (we use the browser Notification API
 *     directly).
 *
 * The SW lives at /sw.js so it can claim the entire origin's scope.
 * In dev we skip registration so Vite's HMR keeps working.
 */

import { bootProbe } from '../observability/telemetry';

declare const __APP_VERSION__: string;

const SW_URL = '/sw.js';
const SW_SCOPE = '/';

export function isServiceWorkerSupported(): boolean {
  return typeof navigator !== 'undefined' && 'serviceWorker' in navigator;
}

export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!isServiceWorkerSupported()) return null;
  if (!import.meta.env.PROD) return null; // skip in dev (HMR breaks otherwise)

  try {
    const reg = await navigator.serviceWorker.register(SW_URL, { scope: SW_SCOPE });
    bootProbe('sw_registered');

    // If a new SW is waiting (deploy rolled out), take over so the
    // user gets the new shell on next navigation without needing to
    // hard-reload. The `controllerchange` listener (added in main.tsx)
    // never reloads: it asks the new-build check, whose banner waits
    // for the operator (`shell/new-build.ts`).
    if (reg.waiting) {
      bootProbe('sw_waiting');
      reg.waiting.postMessage({ type: 'SKIP_WAITING' });
    }

    reg.addEventListener('updatefound', () => {
      const installing = reg.installing;
      if (!installing) return;
      installing.addEventListener('statechange', () => {
        if (installing.state === 'installed' && navigator.serviceWorker.controller) {
          // New SW installed but waiting for old SW tabs to close.
          // Tell the user via a soft event: the new-build banner
          // (`shell/new-build.ts`) listens for it.
          window.dispatchEvent(new CustomEvent('nexsoss:sw-update-ready'));
        }
      });
    });

    return reg;
  } catch (err) {
     
    console.warn('[sw] registration failed', err);
    return null;
  }
}

/**
 * Build-time stamp the SW uses to evict old caches when a new shell
 * is deployed. The value comes from `__APP_VERSION__` (Vite `define`)
 * which the Dockerfile sets to the git SHA at build time.
 */
export const SW_CACHE_NAME = `nexsoss-shell-v1-${(typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : 'dev').slice(0, 12)}`;
